import React, { createContext, useContext, useRef } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface SectionScopeInfo {
  memberId?: number;
  tramsAccountId?: number;
  role?: string;
  viewMode?: "my" | "all";
}

export const SectionScopeContext = createContext<SectionScopeInfo>({});

function formatScopeForLog(scope: SectionScopeInfo): string {
  const parts: string[] = [];
  if (scope.memberId != null) parts.push("hasMember=true");
  if (scope.tramsAccountId != null) parts.push("hasTramsAccount=true");
  if (scope.role) parts.push(`role=${scope.role}`);
  if (scope.viewMode) parts.push(`viewMode=${scope.viewMode}`);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  sectionName: string;
  onRetry?: () => void;
}

export class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  static contextType = SectionScopeContext;
  declare context: React.ContextType<typeof SectionScopeContext>;

  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const scope = this.context ?? {};
    console.error(
      `[${this.props.sectionName}] Section error${formatScopeForLog(scope)}:`,
      error,
      info,
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
          role="alert"
          aria-label={`${this.props.sectionName} failed to load`}
        >
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-sm font-medium text-destructive mb-1">
            {this.props.sectionName} failed to load
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function SectionLoadError({
  sectionName,
  error,
  onRetry,
}: {
  sectionName: string;
  error: Error | string;
  onRetry: () => void;
}) {
  const scope = useContext(SectionScopeContext);
  const message = typeof error === "string" ? error : error.message;
  const loggedRef = useRef<string | null>(null);

  React.useEffect(() => {
    const key = `${sectionName}::${message}`;
    if (loggedRef.current === key) return;
    loggedRef.current = key;
    console.error(
      `[${sectionName}] Load error${formatScopeForLog(scope)}:`,
      message,
    );
  }, [sectionName, message, scope]);

  return (
    <div
      className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
      role="alert"
      aria-label={`${sectionName} failed to load`}
    >
      <AlertCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
      <p className="text-sm font-medium text-destructive mb-3">
        Failed to load {sectionName.toLowerCase()}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        Retry
      </Button>
    </div>
  );
}
